/**
 * usePricing Hooks (S9.1 + S5.1) — 定價設定 screens + product 定價 section.
 *
 * Cache contract: ONE static cacheKey (`/api/pricing/dataset`) hydrates the
 * pricing store on first load; after that the STORE dataset is authoritative
 * for the session (store-first) because the S1.5 MSW layer is read-only —
 * see the seam note in `src/stores/pricingStore.ts`. Mutations still clear
 * the cacheKey so a future handler-backed refetch slots straight in.
 *
 * All derived numbers come from the shared pure resolver building blocks
 * (`src/lib/pricing`) — never re-derived locally, so the screens can never
 * drift from the SO-line resolve math.
 *
 * S5.1 (plan §S5.1): exposes the schedule / group / currency-matrix views the
 * extended PricingSection renders — every builder is a PURE selection over the
 * dataset via `src/lib/pricing` modules (no price arithmetic in hooks; the
 * engine computes, the hook selects, the tsx formats). The PL × currency tab
 * selection persists PER USER in localStorage (DD!128–131 使用者選擇; Q-T2).
 */

import { useEffect, useMemo, useState } from 'react';
import { clearCache, useRequest } from 'ahooks';
import dayjs from 'dayjs';
import {
  anchorFigureOf,
  anchorListPrice,
  buildCostBreakdown,
  buildUpWaterfall,
  effectiveAnchorOf,
  effectiveBandGroupOf,
  resolvePrice,
  resolvePriceGroup,
  roundHalfUp2,
  selectCostRecord,
} from '@/lib/pricing';
import { selectEstimatedCost } from '@/lib/pricing/costEstimate';
import { resolveCurrencyCell } from '@/lib/pricing/currencyMatrix';
import { findProductPricingGroup } from '@/lib/pricing/groups';
import {
  findQuantitySchedule,
  getEffectiveQuantityBrackets,
  isOutsideSchedulePeriod,
} from '@/lib/pricing/quantityPricing';
import { buildQuantityMatrix } from '@/lib/pricing';
import type { QuantityMatrixView } from '@/lib/pricing';
import { freshnessTierOf, isStaleTier } from '@/lib/reorder/staleness';
import { pricingApi } from '@/services/api/pricing.api';
import { getUserProfile } from '@/services/api/axios.config';
import { can } from '@/lib/rbac';
import { FINANCE_COST_BASIS_EDIT } from '@/constants/routePermissions';
import type { User } from '@/models/user';
import { useItemLocations } from '@/hooks/useItemLocations';
import {
  SPECIAL_PRICE_BATCH_CAP,
  applySpecialPriceSave,
  expandSpecialPriceBatchCombos,
  hasBlockingActiveSpecial,
  hasResolvableCostBase,
  usePricingStore,
} from '@/stores/pricingStore';
import type {
  CostRecordCreate,
  CostRecordPatch,
  MarkupRuleEdit,
  PriceGroupBandCreate,
  PriceGroupBandPatch,
  PriceOverrideAuditRow,
  PricingGroupPatch,
  SpecialPriceBatchAdjustment,
  SpecialPriceBatchInput,
} from '@/stores/pricingStore';
import type {
  CostBreakdown,
  CostEstimateInputs,
  CostEstimateSelection,
  CostEstimateType,
  CostRecord,
  CurrencyCellResolution,
  CurrencyPriceRow,
  CustomerGroupDiscount,
  CustomerGroupPctDiscount,
  MarkupRule,
  PriceGroupBand,
  PriceLevel,
  PriceResolution,
  PriceSource,
  PriceWaterfallRow,
  CostingMethodDefault,
  PricingAnchor,
  PricingDataset,
  PricingGroup,
  ProductPricingFields,
  QuantityBracket,
  QuantityPricingSchedule,
  QuantityPricingScope,
  SpecialItemPrice,
} from '@/types/api/pricing';
import type { Product, ProductVendorLine } from '@/types/api/product';
import type {
  NetSuiteItemLocationOnHandTotals,
  NetSuiteItemLocationTotals,
} from '@/types/api/purchaseOrder';

export const PRICING_DATASET_CACHE_KEY = '/api/pricing/dataset';

export const PRICE_SOURCES: readonly PriceSource[] = ['CH', 'TW'] as const;

/** Today's ISO date — the as-of every W9 screen resolves at. */
export const pricingAsOf = (): string => dayjs().format('YYYY-MM-DD');

// ---------------------------------------------------------------------------
// 成本基準 edit gate — the DEDICATED `finance.costBasis.edit` permission (Open
// Q3 default), resolver-wired in S1.3. This was the odd role-ONLY site (admin
// || manager, no permission fallback); MANAGER stops being a magic role and the
// gate becomes the pure Axis-A rbac read (`can`, null-safe) over the stored
// profile snapshot. Behaviour-preserving by the no-lockout seed (S0.5): the
// permission is seeded onto EVERY role that edits cost basis today (role-admin,
// Procurement Manager, Finance Manager; Branch Manager by inheritance) — frozen
// by `src/lib/rbac/__tests__/noLockoutParity.test.ts`.
// ---------------------------------------------------------------------------

export function canEditCostBasis(): boolean {
  return can(getUserProfile() as User | null, FINANCE_COST_BASIS_EDIT);
}

// ---------------------------------------------------------------------------
// Dataset read hook
// ---------------------------------------------------------------------------

/**
 * The ONE dataset read every W9 screen and the product 定價 section hydrate
 * from — `pricingApi.getDataset()`, whole.
 *
 * INTERIM REMOVED 2026-08-10 (trigger: `b42f233`, met). S5.1 folded five raw
 * `/pricing/*` GETs in beside `getDataset()` because the facade assembled six
 * collections and the S4.1 screens needed eleven. `b42f233` gave all 26 facade
 * methods a live leg and `getDataset()` now assembles ALL ELEVEN — the five in
 * question through `listQuantitySchedules` / `listPricingGroups` /
 * `listGroupDiscounts` / `listCurrencyPriceRows` / `listProductPricing`, each
 * behind its own `isLiveModule('pricing')` graph leg. The P3/D17 engine inputs
 * (`productBandGroups`, `productAnchors`) and the `/pricing/defaults` read that
 * carries `pricingAnchorDefault` / `costingMethodDefault` / `minimumMarginPct`
 * ride the SAME facade call — one fetch, one cacheKey, no second transport.
 *
 * The five raw results were spread LAST. With `pricing` flipped live that made
 * this function fetch each collection TWICE — once over `/graphql`, once over
 * MSW — and keep the mock's copy. A live leg that runs, costs a round trip and
 * is then discarded is worse than one that never ran: the transport looks
 * healthy, the pixels are mock, and no gate can see it (the calls classify
 * `off-surface`, which no gate reads). Deleted rather than re-ordered.
 *
 * Unflipped, the facade's fallback hits the SAME five endpoints, so mock-seam
 * behaviour is unchanged. Guarded by `pricingDatasetCoverage.test.ts`, which
 * asserts both halves: eleven graph legs and zero `apiClient` here.
 */

/**
 * Minimum-margin write sequence (2026-09-16, verifier finding on f631aaf).
 *
 * The floor is SERVER-authoritative, so every successful dataset fetch
 * overlays it onto the store. A GET that STARTED before a PATCH can resolve
 * AFTER it, still carrying the old value — and that overlay silently undid a
 * save the operator had just seen confirmed. Every successful PATCH bumps
 * this sequence; a read may only mirror the floor when no PATCH landed while
 * it was in flight. Module state on purpose: a reload also kills every
 * in-flight request, so there is nothing to persist.
 */
let minimumMarginWriteSeq = 0;
const datasetFetchSeq = new WeakMap<PricingDataset, number>();

/** The dataset fetch, stamped with the write sequence it STARTED under. */
export async function fetchDatasetStamped(): Promise<PricingDataset> {
  const seq = minimumMarginWriteSeq;
  const dataset = await pricingApi.getDataset();
  datasetFetchSeq.set(dataset, seq);
  return dataset;
}

/**
 * Mirror a fetched dataset's floor into the store — ONLY when no PATCH landed
 * while that fetch was in flight (an unstamped dataset never mirrors).
 * Returns whether it mirrored, so the race is testable without React.
 */
export function mirrorFetchedMinimumMargin(dataset: PricingDataset): boolean {
  if (datasetFetchSeq.get(dataset) !== minimumMarginWriteSeq) return false;
  usePricingStore.getState().setMinimumMarginPct(dataset.minimumMarginPct ?? null);
  return true;
}

/**
 * The ONE minimum-margin write, as a plain function (no hook) so a test can
 * drive it against the MSW routes. AWAITED: the store takes the RESPONSE
 * value, never the request value, and a refused PATCH rejects so the control
 * can revert and say so.
 */
export async function patchMinimumMarginPct(value: number | null): Promise<number | null> {
  const { data } = await pricingApi.patchDefaults({ minimumMarginPct: value });
  const landed = data.data.minimumMarginPct ?? null;
  minimumMarginWriteSeq += 1;
  usePricingStore.getState().setMinimumMarginPct(landed);
  clearCache(PRICING_DATASET_CACHE_KEY);
  return landed;
}

/**
 * Re-read the company floor from `/pricing/defaults` (order entry mounts it):
 * the dataset cache can be up to a minute old, and the preview tag must test
 * the floor the save will test. A PATCH that lands meanwhile wins.
 */
let minimumMarginReadInFlight: Promise<number | null> | null = null;

export async function refreshMinimumMarginPct(): Promise<number | null> {
  // One mount can ask several times (the card, the line editor, a focus
  // event); they share the answer instead of firing four identical GETs.
  if (minimumMarginReadInFlight) return minimumMarginReadInFlight;
  const seq = minimumMarginWriteSeq;
  minimumMarginReadInFlight = (async () => {
    try {
      const { data } = await pricingApi.getDefaults();
      const current = data.data.minimumMarginPct ?? null;
      if (seq === minimumMarginWriteSeq) usePricingStore.getState().setMinimumMarginPct(current);
      return current;
    } finally {
      minimumMarginReadInFlight = null;
    }
  })();
  return minimumMarginReadInFlight;
}

export function usePricingDataset() {
  const stored = usePricingStore((state) => state.dataset);
  const hydrate = usePricingStore((state) => state.hydrate);

  const { data, error, loading, run } = useRequest(() => fetchDatasetStamped(), {
    cacheKey: PRICING_DATASET_CACHE_KEY,
    staleTime: 60000,
    cacheTime: 300000,
    onSuccess: (dataset) => {
      hydrate(dataset);
      // Minimum margin (2026-09-15) is SERVER-authoritative: the SO handler
      // tests the handler-side row, so a persisted local copy that outlived a
      // dev-server restart must never make the card say a number the hold
      // does not enforce. Overlaid on a successful fetch ONLY when no PATCH
      // landed while it was in flight (see `minimumMarginWriteSeq`).
      mirrorFetchedMinimumMargin(dataset);
    },
  });

  return {
    /** Store wins once hydrated — local edits are never clobbered by refetch. */
    dataset: stored ?? data ?? null,
    error,
    isLoading: loading && stored === null,
    /** Invalidate + re-run (hydrate only fills an EMPTY store — see store seam). */
    refresh: () => {
      clearCache(PRICING_DATASET_CACHE_KEY);
      return run();
    },
  };
}

/**
 * The company minimum margin as the STORE knows it (hydrating the dataset on
 * first use so an SO page opened cold still reads the same floor the handler
 * will test). `null` ⇒ no floor ⇒ every floor-aware surface renders nothing.
 */
export function useMinimumMarginPct(): number | null {
  const { dataset } = usePricingDataset();
  return dataset?.minimumMarginPct ?? null;
}

/**
 * Order entry (SO create / modify): the company floor, re-read from
 * `/pricing/defaults` on mount so the preview tests the number the save will.
 * A failed re-read keeps the cached value — the server still decides on save.
 */
export function useFreshMinimumMarginPct(): number | null {
  const floor = useMinimumMarginPct();
  useEffect(() => {
    const refresh = () => {
      refreshMinimumMarginPct().catch(() => undefined);
    };
    refresh();
    // Another seat may change the floor while this form is open; returning to
    // the tab re-reads it, so the preview does not sit on a minute-old cache.
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);
  return floor;
}

/** PRICE_OVERRIDE review rows (newest first) — the S9.1 audit lens feed. */
export function usePriceOverrideAudit(): PriceOverrideAuditRow[] {
  return usePricingStore((state) => state.auditRows);
}

/**
 * S1.1 "Reset demo data" (DevFlagPanel): drop every local demo edit — the
 * store slice (whose S1.1 `persist` overlay follows it) AND the ahooks cache —
 * then re-run the dataset fetch so the seeds land back through the normal
 * hydrate path. Dev/mock-mode affordance only (the panel itself is DEV-only).
 */
export async function resetDemoPricing(): Promise<void> {
  usePricingStore.getState().reset();
  clearCache(PRICING_DATASET_CACHE_KEY);
  // Minimum margin (2026-09-15) lives on the mock "server" too (its own
  // localStorage overlay, so it survives a reload) — a demo reset clears both
  // sides, or the seed would come back with a floor nobody typed this session.
  try {
    await pricingApi.patchDefaults({ minimumMarginPct: null });
  } catch {
    // A non-manager session is refused (403) — the rest of the reset still runs.
  }
  const dataset = await pricingApi.getDataset();
  usePricingStore.getState().hydrate(dataset);
}

// ---------------------------------------------------------------------------
// Mutations — store-first local writes (read-only MSW seam; see store docblock)
// ---------------------------------------------------------------------------

export function usePricingMutations() {
  const upsertMarkupRules = usePricingStore((state) => state.upsertMarkupRules);
  const addPriceLevel = usePricingStore((state) => state.addPriceLevel);
  const saveSpecialPriceAction = usePricingStore((state) => state.saveSpecialPrice);
  const setSpecialPriceActiveAction = usePricingStore((state) => state.setSpecialPriceActive);
  const saveSpecialPriceBatchAction = usePricingStore((state) => state.saveSpecialPriceBatch);
  const updateCostRecordAction = usePricingStore((state) => state.updateCostRecord);
  const addCostRecordAction = usePricingStore((state) => state.addCostRecord);
  const setPricingAnchorDefaultAction = usePricingStore((state) => state.setPricingAnchorDefault);
  const setCostingMethodDefaultAction = usePricingStore((state) => state.setCostingMethodDefault);
  const saveProductPricingAction = usePricingStore((state) => state.saveProductPricing);
  const addPricingGroupAction = usePricingStore((state) => state.addPricingGroup);
  const saveQuantityScheduleAction = usePricingStore((state) => state.saveQuantitySchedule);
  const updatePricingGroupAction = usePricingStore((state) => state.updatePricingGroup);
  const setPricingGroupActiveAction = usePricingStore((state) => state.setPricingGroupActive);
  const saveCustomerGroupDiscountAction = usePricingStore(
    (state) => state.saveCustomerGroupDiscount
  );
  const saveCurrencyPriceRowAction = usePricingStore((state) => state.saveCurrencyPriceRow);
  const addPriceGroupBandAction = usePricingStore((state) => state.addPriceGroupBand);
  const updatePriceGroupBandAction = usePricingStore((state) => state.updatePriceGroupBand);
  const setPriceGroupBandActiveAction = usePricingStore(
    (state) => state.setPriceGroupBandActive
  );
  const moveBandCutAction = usePricingStore((state) => state.moveBandCut);

  const actorName = (): string => (getUserProfile() as User | null)?.name ?? 'unknown';

  const invalidate = () => clearCache(PRICING_DATASET_CACHE_KEY);

  return {
    saveMarkupCell: (edits: MarkupRuleEdit[]) => {
      upsertMarkupRules(edits);
      invalidate();
    },
    createPriceLevel: (level: PriceLevel) => {
      addPriceLevel(level);
      invalidate();
    },
    /** Throws PricingGuardError('REASON_REQUIRED') on a blank reason. */
    saveSpecialPrice: (row: SpecialItemPrice, reason: string) => {
      saveSpecialPriceAction(row, reason, actorName());
      invalidate();
    },
    setSpecialPriceActive: (id: string, isActive: boolean, reason: string) => {
      setSpecialPriceActiveAction(id, isActive, reason, actorName());
      invalidate();
    },
    /**
     * S3.1 central batch — ONE reason covers the whole batch. May throw
     * PricingGuardError('BATCH_EMPTY' | 'BATCH_CAP_EXCEEDED' |
     * 'BATCH_OVERLAP') — the WHOLE batch is refused then, nothing written
     * (refuse-on-overlap, never a partial write). An accepted batch lands
     * one 'special_price_batch' audit header + per-row entries atomically.
     */
    saveSpecialPriceBatch: (input: SpecialPriceBatchInput, reason: string) => {
      saveSpecialPriceBatchAction(input, reason, actorName());
      invalidate();
    },
    /**
     * Cost-basis edit (S1.2) — reason-gated like every price-affecting save
     * (throws PricingGuardError('REASON_REQUIRED') on a blank reason) and
     * audited as a 'cost_record' PRICE_OVERRIDE row.
     */
    updateCostRecord: (recordId: string, patch: CostRecordPatch, reason: string) => {
      updateCostRecordAction(recordId, patch, reason, actorName());
      invalidate();
    },
    /**
     * S2.4 cost-record CREATE — the sealed-legal 來源 write path (source is
     * chosen at create ONLY; read-only everywhere after). Reason-gated like
     * every price-affecting save; audited as 'cost_record'. May throw
     * PricingGuardError('DUPLICATE_RECORD' | 'PRODUCT_NOT_FOUND' |
     * 'NEGATIVE_COST_INPUT') — nothing is written then. AUD-only by
     * construction (D27): the create shape has no currency member.
     * `knownProductIds` = the option list the caller's product picker
     * rendered (the pricing dataset has no product master to check against).
     */
    addCostRecord: (
      create: CostRecordCreate,
      knownProductIds: readonly string[],
      reason: string
    ) => {
      addCostRecordAction(create, knownProductIds, reason, actorName());
      invalidate();
    },
    /** D17 — company-wide Pricing Anchor default (Price Setup owns the control). */
    setPricingAnchorDefault: (anchor: PricingAnchor) => {
      setPricingAnchorDefaultAction(anchor);
      invalidate();
    },
    /** Company costing-method default — Price Setup sets it, create carries it. */
    setCostingMethodDefault: (method: CostingMethodDefault) => {
      setCostingMethodDefaultAction(method);
      invalidate();
    },
    /**
     * Company minimum margin (2026-09-15). NOT store-first: the SO handler
     * derives the low-margin hold from the handler-side row, so the PATCH is
     * the write and the store mirrors its response. A failed PATCH rejects —
     * the control reverts and says so; no success is ever shown before the
     * write is real (ux-review 2026-09-15).
     */
    setMinimumMarginPct: (value: number | null): Promise<number | null> =>
      patchMinimumMarginPct(value),
    /**
     * Per-product pricing assignment (schedule XOR ad-hoc, customer scope).
     * Price-affecting ⇒ throws PricingGuardError('REASON_REQUIRED') on a
     * blank reason, exactly like the other S4.1 saves.
     */
    saveProductPricing: (fields: ProductPricingFields, reason: string) => {
      saveProductPricingAction(fields, reason, actorName());
      invalidate();
    },
    /**
     * S1.4 consolidation — group CREATION is not price-affecting until a card
     * references it, so no reason gate (mirrors createPriceLevel). Cache
     * invalidation rides the wrapper, never the call site.
     */
    addPricingGroup: (group: PricingGroup) => {
      addPricingGroupAction(group);
      invalidate();
    },
    /**
     * S1.4 consolidation — schedule save IS price-affecting: reason-gated
     * (throws PricingGuardError('REASON_REQUIRED') on a blank reason) with
     * actor attribution supplied HERE, so no surface reads the user profile
     * for a write path again.
     */
    saveQuantitySchedule: (schedule: QuantityPricingSchedule, reason: string) => {
      saveQuantityScheduleAction(schedule, reason, actorName());
      invalidate();
    },
    /**
     * S2.1 group edit (names / policy rate / taxonomy link — the patch type
     * has no code or isActive member, so the immutable code stays immutable
     * at type level). Reason-gated: throws
     * PricingGuardError('REASON_REQUIRED') on a blank reason,
     * 'RECORD_NOT_FOUND' on an unknown group.
     */
    updatePricingGroup: (groupId: string, patch: PricingGroupPatch, reason: string) => {
      updatePricingGroupAction(groupId, patch, reason, actorName());
      invalidate();
    },
    /** S2.1 deactivate/activate — no hard delete; reason-gated + audited. */
    setPricingGroupActive: (groupId: string, isActive: boolean, reason: string) => {
      setPricingGroupActiveAction(groupId, isActive, reason, actorName());
      invalidate();
    },
    /**
     * S2.3 customer×group 折扣卡 save (add AND edit — the reducer upserts by
     * the (customerId, groupId) pair, Discribe-20 one-card-per-pair). Reason
     * -gated like every price-affecting save; audited as
     * 'customer_group_discount'.
     */
    saveCustomerGroupDiscount: (cardRow: CustomerGroupDiscount, reason: string) => {
      saveCustomerGroupDiscountAction(cardRow, reason, actorName());
      invalidate();
    },
    /**
     * S2.2 currency-row save (add / unlock / price edit — ONE reducer, keyed
     * by the product × currency × level cell). Reason-gated like every
     * price-affecting save; may throw
     * PricingGuardError('FIXED_PRICE_LOCKED') on a price change to a locked
     * row (Discribe-21 unlock-first) — nothing is written then. Audited as
     * 'currency_price'.
     */
    saveCurrencyPriceRow: (priceRow: CurrencyPriceRow, reason: string) => {
      saveCurrencyPriceRowAction(priceRow, reason, actorName());
      invalidate();
    },
    /**
     * P2 band CRUD — create a cut. Price-affecting under RULING R-BAND (a
     * band CHOOSES the markup rule a level price is computed from), so it is
     * reason-gated like every other price-affecting save, unlike
     * `addPricingGroup`. May throw PricingGuardError('REASON_REQUIRED' |
     * 'DUPLICATE_RECORD' | 'BAND_RANGE_INVALID' | 'BAND_OPEN_END_NOT_TOP' |
     * 'BAND_OVERLAP' | 'BAND_GAP') — nothing is written then, and the
     * band-set violations carry a `detail` payload naming the two cuts and
     * the two numbers that conflict.
     */
    addPriceGroupBand: (create: PriceGroupBandCreate, reason: string) => {
      addPriceGroupBandAction(create, reason, actorName());
      invalidate();
    },
    /**
     * P2 band CRUD — edit the cuts. The patch type has no currency,
     * productGroupId or isActive member, so a band never migrates between
     * sets and activation is never a side effect of an edit.
     */
    updatePriceGroupBand: (bandId: string, patch: PriceGroupBandPatch, reason: string) => {
      updatePriceGroupBandAction(bandId, patch, reason, actorName());
      invalidate();
    },
    /**
     * P2 band CRUD — deactivate/activate. No hard delete; refused with
     * 'BAND_GAP' when it would hole the cover (widen a neighbour first).
     */
    setPriceGroupBandActive: (bandId: string, isActive: boolean, reason: string) => {
      setPriceGroupBandActiveAction(bandId, isActive, reason, actorName());
      invalidate();
    },
    /**
     * P3 band CRUD — move a SHARED CUT: the edited band AND the touching
     * neighbour whose matching bound is the same number, in one atomic write.
     * The surface must have previewed the plan (`planBandCutMoves`) first —
     * this is a disclosed two-row move, never a silent repair.
     */
    moveBandCut: (bandId: string, patch: PriceGroupBandPatch, reason: string) => {
      moveBandCutAction(bandId, patch, reason, actorName());
      invalidate();
    },
  };
}

// ---------------------------------------------------------------------------
// S3.1 — batch special-price preview (pure builder, exported for vitest)
// ---------------------------------------------------------------------------

/** The form's live state — adjustment may still be blank while picking. */
export interface SpecialPriceBatchDraft {
  productIds: string[];
  customerIds: number[];
  adjustment: SpecialPriceBatchAdjustment | null;
  validFrom: string;
  validTo: string | null;
}

export interface SpecialPriceBatchPreviewRow {
  key: string;
  productId: string;
  customerId: number;
  /**
   * The pair already carries ANY active special (window ignored, RULING-5)
   * — blocks Apply.
   */
  overlap: boolean;
  /**
   * FACTUAL per-product flag (RULING-6): no cost record resolvable at
   * `proposedAsOf`. Blocking ONLY for markup_pct drafts — a freeze needs no
   * base.
   */
  noCostRecord: boolean;
  /** CURRENT resolved price for the combo (the @/lib/pricing shim, as-of today). */
  current: PriceResolution;
  /**
   * Resolution WITH the candidate row applied (engine-computed — never local
   * arithmetic). `null` while the adjustment is blank, the row is
   * overlap-blocked, or a markup draft has no cost base (RULING-6).
   */
  proposed: PriceResolution | null;
}

export interface SpecialPriceBatchPreview {
  comboCount: number;
  /** Named cap-exceeded state — over the cap NOTHING is resolved (rows = []). */
  capExceeded: boolean;
  overlapCount: number;
  /**
   * RULING-6: rows where `noCostRecord` AND the draft adjustment is
   * markup_pct — 0 while the adjustment is blank or fixed. Blocks Apply;
   * agrees with the reducer's BATCH_UNRESOLVABLE by construction (same
   * predicate, same date clamp).
   */
  unresolvableCount: number;
  /** Exactly the rows the batch would write, in write order; [] until combos exist. */
  rows: SpecialPriceBatchPreviewRow[];
}

/**
 * Build the batch preview: one row per combo (the SAME deterministic
 * expansion the reducer writes), each carrying the current resolved price
 * next to the engine-resolved proposed value, with overlap combos flagged.
 * Over the hard cap the preview REFUSES to resolve (a preview that
 * times-out-or-lies is the refuted materialization failure mode) — the named
 * cap-exceeded state replaces the table.
 */
export function buildSpecialPriceBatchPreview(
  dataset: PricingDataset,
  draft: SpecialPriceBatchDraft,
  asOf: string = pricingAsOf()
): SpecialPriceBatchPreview {
  const comboCount = draft.productIds.length * draft.customerIds.length;
  if (comboCount === 0) {
    return { comboCount, capExceeded: false, overlapCount: 0, unresolvableCount: 0, rows: [] };
  }
  if (comboCount > SPECIAL_PRICE_BATCH_CAP) {
    return { comboCount, capExceeded: true, overlapCount: 0, unresolvableCount: 0, rows: [] };
  }
  // A future-dated window previews AT its own start — resolving before
  // validFrom would skip the candidate as outside_validity and show a lie.
  // The RULING-6 cost-base check clamps to the SAME date (preview flag ⇔
  // reducer refusal agree by construction).
  const proposedAsOf = draft.validFrom > asOf ? draft.validFrom : asOf;
  const rows = expandSpecialPriceBatchCombos(draft.productIds, draft.customerIds).map((combo) => {
    const overlap = hasBlockingActiveSpecial(dataset, combo.customerId, combo.productId);
    const noCostRecord = !hasResolvableCostBase(dataset, combo.productId, proposedAsOf);
    const current = resolvePrice(
      { productId: combo.productId, customerId: combo.customerId, asOf },
      dataset
    );
    let proposed: PriceResolution | null = null;
    if (
      !overlap &&
      draft.adjustment !== null &&
      !(noCostRecord && draft.adjustment.kind === 'markup_pct')
    ) {
      const base = {
        id: 'sip-batch-preview',
        customerId: combo.customerId,
        productId: combo.productId,
        reason: '',
        validFrom: draft.validFrom,
        validTo: draft.validTo,
        isActive: true,
        createdBy: '',
        createdAt: '',
      };
      const candidate: SpecialItemPrice =
        draft.adjustment.kind === 'fixed'
          ? {
              ...base,
              kind: 'fixed',
              fixedPrice: draft.adjustment.fixedPrice,
              currency: draft.adjustment.currency,
            }
          : {
              ...base,
              kind: 'markup_pct',
              markupPct: draft.adjustment.markupPct,
              // B0 — the PREVIEW candidate must be stamped exactly like
              // the row the batch will actually write
              // (`buildSpecialPriceBatchRows`, src/stores/pricingStore.ts):
              // a preview that resolves off a different basis than the
              // saved row is the divergence this field exists to stop.
              // Option B (2026-08-31): the EFFECTIVE anchor, not the literal
              // — the resolver now multiplies the figure this names.
              basisAtAuthoring: effectiveAnchorOf(dataset, combo.productId),
            };
      proposed = resolvePrice(
        { productId: combo.productId, customerId: combo.customerId, asOf: proposedAsOf },
        applySpecialPriceSave(dataset, candidate)
      );
    }
    return {
      key: `${combo.productId}|${combo.customerId}`,
      productId: combo.productId,
      customerId: combo.customerId,
      overlap,
      noCostRecord,
      current,
      proposed,
    };
  });
  return {
    comboCount,
    capExceeded: false,
    overlapCount: rows.filter((row) => row.overlap).length,
    unresolvableCount:
      draft.adjustment?.kind === 'markup_pct'
        ? rows.filter((row) => row.noCostRecord).length
        : 0,
    rows,
  };
}

// ---------------------------------------------------------------------------
// Derived views (pure, memoized) — matrix cells, cost rows, product pricing
// ---------------------------------------------------------------------------

/** Rule lookup keyed `${priceGroupId}|${source}|${priceLevelId}`. */
export function markupRuleIndex(rules: MarkupRule[]): Map<string, MarkupRule> {
  return new Map(rules.map((rule) => [`${rule.priceGroupId}|${rule.source}|${rule.priceLevelId}`, rule]));
}

export interface CostBasisRow {
  record: CostRecord;
  breakdown: CostBreakdown;
  /**
   * The product's effective Pricing Anchor — carried so the row can print the
   * ANCHORED list price rather than the base-anchored one.
   */
  anchor: PricingAnchor;
  /**
   * P3 FIX (2026-08-31) — the list price under THIS product's anchor, i.e.
   * `anchorListPrice(breakdown, anchor)`, the same function the product page's
   * List Price chip and the resolver's closing trace row use.
   *
   * ⚠ `breakdown.listPrice` is ALWAYS base-anchored. Since the Option-B flip
   * (`8cfb9d1`) the product page prints `anchorListPrice`, so on any product
   * whose anchor is not `pricing_base` the Cost Basis column labelled "List
   * Price" printed a DIFFERENT number from the product page's chip under the
   * SAME label — measured on `prod-fog-cover-01` (anchor `purchase_cost`),
   * whose base-anchored figure is 140.00 and whose anchored figure is 127.40.
   * The screen a buyer reads and the screen an operator sets prices on must
   * not disagree about the list price, so this is the ONE computed figure and
   * `breakdown.listPrice` is no longer read by any surface column.
   */
  listPrice: number;
}

/** Cost records with their derived build-up chain (derive-never-restore). */
export function useCostBasisRows(): {
  rows: CostBasisRow[];
  isLoading: boolean;
  error: unknown;
  refresh: () => void;
} {
  const { dataset, isLoading, error, refresh } = usePricingDataset();
  const rows = useMemo<CostBasisRow[]>(
    () =>
      (dataset?.costRecords ?? []).map((record) => {
        const breakdown = buildCostBreakdown(record);
        // Per-record, because the anchor is per PRODUCT and this table lists
        // records across products — reading one anchor for the whole table
        // would print one product's basis under another product's row.
        const anchor = dataset ? effectiveAnchorOf(dataset, record.productId) : 'pricing_base';
        return { record, breakdown, anchor, listPrice: anchorListPrice(breakdown, anchor) };
      }),
    [dataset]
  );
  return { rows, isLoading, error, refresh };
}

export interface ProductLevelPrice {
  level: PriceLevel;
  markupPct: number | null;
  /** pricingBase × (1 + markupPct/100), 2dp — null when no rule exists. */
  unitPrice: number | null;
  /** Margin vs landed in % of unit price, 2dp. */
  marginPct: number | null;
}

export interface ProductSourcePricing {
  source: PriceSource;
  record: CostRecord;
  breakdown: CostBreakdown;
  band: PriceGroupBand | null;
  /**
   * The EFFECTIVE Pricing Anchor this pane's ladder multiplied (Option B,
   * 2026-08-31) — the product's own pick, else the company default, else
   * `pricing_base`.
   */
  anchor: PricingAnchor;
  /** `anchorFigureOf(breakdown, anchor)` — the figure every markup below multiplies. */
  anchorFigure: number;
  /**
   * List Price UNDER THE ANCHOR (`anchorFigure × (1 + 銷售加價率)`). Not
   * `breakdown.listPrice`, which is always the base-anchored figure: the chip
   * and the Price Trace's closing row must print the same number, and the
   * trace comes from the resolver.
   */
  listPrice: number;
  levelPrices: ProductLevelPrice[];
  /** List-price resolution (no customer) for the margin footer. */
  listResolution: PriceResolution;
  /**
   * S5.1 — the labeled build-up stage list (engine-computed labels, formulas
   * AND amounts via `buildUpWaterfall`; the tsx only formats — grep-gate).
   */
  waterfallRows: PriceWaterfallRow[];
  /**
   * 售價矩陣預覽 (2026-08-28) — price-level rows × quantity-tier columns of
   * FINAL unit prices. Strictly derived, never stored or editable.
   */
  quantityMatrix: QuantityMatrixView;
}

// ---------------------------------------------------------------------------
// S5.1 — quantity-schedule / pricing-group / currency-matrix views
// (pure builders exported for vitest — pricingSectionMount.test.tsx)
// ---------------------------------------------------------------------------

export interface ProductQuantityPricingView {
  /** 'schedule' = DD!116 reusable schedule; 'ad_hoc' = product brackets; 'none' = explicit empty state. */
  mode: 'schedule' | 'ad_hoc' | 'none';
  schedule: QuantityPricingSchedule | null;
  brackets: QuantityBracket[];
  /** Who the brackets reach (2026-08-28); absent field ⇒ 'all_customers'. */
  scope: QuantityPricingScope;
  /**
   * The product references a dated schedule whose 適用期間 has NOT started or
   * has ENDED as of today — brackets exist but contribute nothing. Rendered
   * as its own honest state, never as "no quantity pricing".
   */
  outsidePeriod: boolean;
}

/** Read-only quantity-pricing view — XOR semantics via the engine's own selector. */
export function buildQuantityPricingView(
  dataset: PricingDataset,
  productId: string,
  asOf: string = pricingAsOf()
): ProductQuantityPricingView {
  const fields = dataset.productPricing?.find((p) => p.productId === productId);
  const scope: QuantityPricingScope = fields?.quantityScope ?? 'all_customers';
  const brackets = getEffectiveQuantityBrackets(
    fields,
    dataset.quantityPricingSchedules,
    asOf
  );
  const outsidePeriod = isOutsideSchedulePeriod(fields, dataset.quantityPricingSchedules, asOf);
  if (outsidePeriod) {
    // Keep the schedule and its brackets visible — the operator needs to see
    // WHICH schedule is dormant and why, not an empty card.
    const dormant = findQuantitySchedule(
      dataset.quantityPricingSchedules,
      fields?.quantityScheduleId ?? ''
    );
    return {
      mode: 'schedule',
      schedule: dormant,
      brackets: dormant?.brackets ?? [],
      scope,
      outsidePeriod: true,
    };
  }
  if (brackets === null || fields === undefined) {
    return { mode: 'none', schedule: null, brackets: [], scope, outsidePeriod: false };
  }
  const scheduleId = fields.quantityScheduleId ?? null;
  if (scheduleId !== null && scheduleId !== '') {
    return {
      mode: 'schedule',
      schedule: findQuantitySchedule(dataset.quantityPricingSchedules, scheduleId),
      brackets,
      scope,
      outsidePeriod: false,
    };
  }
  return { mode: 'ad_hoc', schedule: null, brackets, scope, outsidePeriod: false };
}

export interface ProductPricingGroupView {
  /** The product's ONE group (Discribe-20 一對一); null = explicit empty state. */
  group: PricingGroup | null;
  /** 大潤發-shape customer 折扣卡 examples for this group (cited pct variant only). */
  discountExamples: CustomerGroupPctDiscount[];
}

export function buildPricingGroupView(
  dataset: PricingDataset,
  productId: string
): ProductPricingGroupView {
  const fields = dataset.productPricing?.find((p) => p.productId === productId);
  const group = findProductPricingGroup(fields, dataset.pricingGroups);
  if (group === null) return { group: null, discountExamples: [] };
  const discountExamples = (dataset.customerGroupDiscounts ?? []).filter(
    (row): row is CustomerGroupPctDiscount =>
      row.groupId === group.id && row.kind === 'discount_pct'
  );
  return { group, discountExamples };
}

export interface CurrencyMatrixRow {
  key: string;
  /** null = the list-price (no-level) row. */
  level: PriceLevel | null;
  /** Locked row or the explicit `no_currency_price` unresolved state. */
  cell: CurrencyCellResolution;
}

/**
 * Tab set for the PL × currency matrix: every currency with at least one
 * locked row anywhere in the dataset, first-seen order (base currency first
 * by seed convention). Dataset-wide ON PURPOSE: a product missing a currency
 * must still show that tab so its cells render the `no_currency_price`
 * unresolved state (glyph + text) instead of the tab silently vanishing.
 */
export function currencyMatrixCurrencies(dataset: PricingDataset): string[] {
  return Array.from(new Set((dataset.currencyPriceRows ?? []).map((row) => row.currency)));
}

/** One matrix column (the selected currency) resolved per level via the engine. */
export function buildCurrencyMatrixRows(
  dataset: PricingDataset,
  productId: string,
  currency: string
): CurrencyMatrixRow[] {
  const levels = dataset.priceLevels
    .filter((level) => level.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  return [null, ...levels].map((level) => ({
    key: level?.id ?? 'list',
    level,
    cell: resolveCurrencyCell(dataset.currencyPriceRows, productId, level?.id ?? null, currency),
  }));
}

/**
 * Per-user persisted currency-tab storage key (DD!128–131 使用者選擇 → the
 * S5.1 currency tab bar; persistence mechanism = localStorage, Q-T2).
 */
export function matrixCurrencyStorageKey(): string {
  const userId = (getUserProfile() as User | null)?.id ?? 'anonymous';
  return `pricing.matrixCurrencyTab.${userId}`;
}

const readStoredMatrixCurrency = (): string | null => {
  try {
    return localStorage.getItem(matrixCurrencyStorageKey());
  } catch {
    return null;
  }
};

const storeMatrixCurrency = (currency: string): void => {
  try {
    localStorage.setItem(matrixCurrencyStorageKey(), currency);
  } catch {
    // Persistence is best-effort — the in-memory selection still applies.
  }
};

export interface ProductCurrencyMatrixView {
  currencies: string[];
  activeCurrency: string | null;
  setActiveCurrency: (currency: string) => void;
  rows: CurrencyMatrixRow[];
  /**
   * Currency-mode list resolution (no customer) for the active tab — the
   * PriceTracePanel feed; carries `currencySkipReason: 'no_currency_price'`
   * when the whole column is unresolved.
   */
  listResolution: PriceResolution | null;
}

export interface ProductPricingView {
  sources: ProductSourcePricing[];
  overrides: SpecialItemPrice[];
  quantityPricing: ProductQuantityPricingView;
  pricingGroup: ProductPricingGroupView;
  matrix: ProductCurrencyMatrixView;
  isLoading: boolean;
  error: unknown;
}

// Exported for the D27 cross-currency isolation suite (pricingCurrencyBandGuard):
// the display seat's band call site must be red-able independently of the engine's.
//
// PAIRED CALL SITE — keep in sync with `attemptCustomerLevel` in
// packages/domain/src/pricing/resolvePrice.ts, which resolves the same band
// and walks the same markup ladder for the engine. The duplication is
// deliberate (the display seat needs EVERY level, the engine needs one), but
// the band predicate and the `base × (1 + markupPct/100)` formula must move
// together or the screen and the resolved SO line disagree. No import edge
// joins them; the relatedness manifest entry
// erp-web:pricing-resolver--product-pricing-view is what fails a one-sided push.
export function buildSourcePricing(
  dataset: PricingDataset,
  productId: string,
  source: PriceSource,
  asOf: string
): ProductSourcePricing | null {
  const record = selectCostRecord(dataset.costRecords, productId, asOf, source);
  if (!record) return null;
  const breakdown = buildCostBreakdown(record);
  // TWO OPERANDS, DELIBERATELY DIFFERENT — the mirror of the engine's comment
  // in `attemptCustomerLevel`. The BAND is looked up on the pricing base for
  // every product (RULING R-BAND, 2026-08-31: one comparable A–D axis, no
  // product silently re-bucketed by its own anchor); the MARKUP multiplies
  // the anchor's figure. Change one of these here without changing it there
  // and the screen forks from the resolved SO line — which is what the
  // relatedness edge erp-web:pricing-resolver--product-pricing-view and
  // pricingCurrencyBandGuard exist to red.
  //
  // THIRD OPERAND — WHICH BAND SET is eligible, from the product's own group.
  // Same mirror rule: the engine passes `effectiveBandGroupOf(dataset, ...)`
  // here too, and a one-sided omission would put the screen on the default
  // set while the SO line resolves on the scoped one, under identical labels.
  const band = resolvePriceGroup(
    dataset.priceGroupBands,
    breakdown.pricingBase,
    breakdown.currency,
    effectiveBandGroupOf(dataset, productId)
  );
  const anchor = effectiveAnchorOf(dataset, productId);
  const anchorFigure = anchorFigureOf(breakdown, anchor);
  const ruleIndex = markupRuleIndex(dataset.markupRules);
  const levelPrices: ProductLevelPrice[] = dataset.priceLevels
    .filter((level) => level.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((level) => {
      const rule = band ? ruleIndex.get(`${band.id}|${source}|${level.id}`) : undefined;
      if (!rule) return { level, markupPct: null, unitPrice: null, marginPct: null };
      const unitPrice = roundHalfUp2(anchorFigure * (1 + rule.markupPct / 100));
      // Margin stays measured against LANDED COST whatever the anchor — the
      // one number that says whether the price covers what the goods cost.
      // Under a `purchase_cost` anchor it can go negative, and it should.
      const marginPct =
        unitPrice === 0
          ? null
          : roundHalfUp2(((unitPrice - breakdown.landedCost) / unitPrice) * 100);
      return { level, markupPct: rule.markupPct, unitPrice, marginPct };
    });
  const listResolution = resolvePrice({ productId, customerId: null, source, asOf }, dataset);
  const waterfallRows = buildUpWaterfall(breakdown, anchor);
  // 售價矩陣預覽 — per SOURCE, because the ladder itself is per source
  // (CH and TW carry different markup rules, so one shared grid would be a
  // lie). Read-only derived output of the same pure functions the resolver
  // uses; the period gate applies, so a dormant schedule yields a flat grid.
  const quantityView = buildQuantityPricingView(dataset, productId, asOf);
  const quantityMatrix = buildQuantityMatrix(
    levelPrices.map((entry) => ({ level: entry.level, unitPrice: entry.unitPrice })),
    quantityView.outsidePeriod ? [] : quantityView.brackets,
    breakdown.currency
  );
  return {
    source,
    record,
    breakdown,
    band,
    anchor,
    anchorFigure,
    listPrice: anchorListPrice(breakdown, anchor),
    levelPrices,
    listResolution,
    waterfallRows,
    quantityMatrix,
  };
}

const EMPTY_QUANTITY_VIEW: ProductQuantityPricingView = {
  mode: 'none',
  schedule: null,
  brackets: [],
  scope: 'all_customers',
  outsidePeriod: false,
};

const EMPTY_GROUP_VIEW: ProductPricingGroupView = { group: null, discountExamples: [] };

/** Everything the product-detail 定價 section renders, per source. */
export function useProductPricing(productId?: string): ProductPricingView {
  const { dataset, isLoading, error } = usePricingDataset();

  // S5.1 currency tab — per-user persisted selection (falls back to the first
  // available currency when nothing is stored / the stored one disappeared).
  const [storedCurrency, setStoredCurrency] = useState<string | null>(() =>
    readStoredMatrixCurrency()
  );
  const currencies = useMemo(() => (dataset ? currencyMatrixCurrencies(dataset) : []), [dataset]);
  const activeCurrency =
    storedCurrency !== null && currencies.includes(storedCurrency)
      ? storedCurrency
      : (currencies[0] ?? null);
  const setActiveCurrency = (currency: string): void => {
    setStoredCurrency(currency);
    storeMatrixCurrency(currency);
  };

  const view = useMemo(() => {
    if (!dataset || !productId) {
      return {
        sources: [] as ProductSourcePricing[],
        overrides: [] as SpecialItemPrice[],
        quantityPricing: EMPTY_QUANTITY_VIEW,
        pricingGroup: EMPTY_GROUP_VIEW,
        matrixRows: [] as CurrencyMatrixRow[],
        matrixListResolution: null as PriceResolution | null,
      };
    }
    const asOf = pricingAsOf();
    const sources = PRICE_SOURCES.map((source) =>
      buildSourcePricing(dataset, productId, source, asOf)
    ).filter((entry): entry is ProductSourcePricing => entry !== null);
    const overrides = dataset.specialItemPrices.filter((row) => row.productId === productId);
    const quantityPricing = buildQuantityPricingView(dataset, productId);
    const pricingGroup = buildPricingGroupView(dataset, productId);
    const matrixRows =
      activeCurrency !== null ? buildCurrencyMatrixRows(dataset, productId, activeCurrency) : [];
    const matrixListResolution =
      activeCurrency !== null
        ? resolvePrice({ productId, customerId: null, asOf, currency: activeCurrency }, dataset)
        : null;
    return { sources, overrides, quantityPricing, pricingGroup, matrixRows, matrixListResolution };
  }, [dataset, productId, activeCurrency]);

  return {
    sources: view.sources,
    overrides: view.overrides,
    quantityPricing: view.quantityPricing,
    pricingGroup: view.pricingGroup,
    matrix: {
      currencies,
      activeCurrency,
      setActiveCurrency,
      rows: view.matrixRows,
      listResolution: view.matrixListResolution,
    },
    isLoading,
    error,
  };
}

// ---------------------------------------------------------------------------
// Y-S2 — estimated-cost figures (DD!96 type × the DD!97/119/122/44 stored
// figures; yellow-sweep disposition 2026-07-29). FIRST UI consumer of the
// S3.1 `selectEstimatedCost` lib — the hook only CREDITS stored fields and
// calls the lib; no arithmetic here or in the tsx (grep-gate).
// (pure builders exported for vitest — pricingSectionMount.test.tsx)
// ---------------------------------------------------------------------------

/**
 * DD!44 Purchase Price 採購價格 / DD!45 Purchase Price Unit — CREDITING NOTE
 * (the yellow-sweep adjudication a reader looking for a `purchasePrice`
 * product field should find): there is NO canonical product-master
 * purchase-price field and none is added. The dictionary semantics are
 * already carried by existing fields —
 * - the negotiated per-vendor price = `ProductVendorLine.unitCost` (rendered
 *   in VendorLinesSection, feeds PO prefill): THE figure the
 *   `purchase_price` estimate credit reads, via the primary line (the H51
 *   pair owner — the same line that carries 上次/下次採購價);
 * - the unit qualifier (DD!45) = `Product.purchaseUnit` (registry MUST,
 *   create-required, Units section) — vendor lines price per purchase unit.
 * Anti-duplication ruling: a second product-level purchase-price field would
 * drift from the vendor lines it summarizes, so no duplicate ships.
 */
export function primaryVendorLineOf(product: Product): ProductVendorLine | null {
  return product.vendorLines?.find((line) => line.isPrimary) ?? null;
}

/**
 * R6 (RULING-10) default-supplier display — the source tab that OPENS is the
 * pane whose effective record links the product's PRIMARY vendor line; when no
 * pane links the primary vendor (e.g. brake-pad-01's named residual: TW
 * primary, CH-only cost records) fall back to the first existing pane (the
 * pre-R6 behaviour). Both panes stay reachable as tabs — this only picks
 * which one opens. Pure, exported for pricingVendorSourceParity.
 */
export function defaultSourceKeyFor(
  product: Product,
  sources: ProductSourcePricing[]
): PriceSource | null {
  const primaryVendorId = primaryVendorLineOf(product)?.vendorId;
  return (
    sources.find(
      (entry) => entry.record.vendorId != null && entry.record.vendorId === primaryVendorId
    )?.source ??
    sources[0]?.source ??
    null
  );
}

/**
 * Credit the DD!119 平均成本 figure from the on-hand read-through totals (the
 * VALUE-WEIGHTED server figure). Mirrors the `selectOnHandTotalsDisplay`
 * zero-vs-empty gate (ItemLocationsSection, plan §S6.1): a stale or empty
 * read-through yields null (dash — never zero-as-data), and the server's
 * ΣqtyOnHand === 0 sentinel (averageCost 0) is NO-DATA, never a cost of 0.00.
 * Pure field selection over the server-computed echo — no arithmetic.
 */
export function creditOnHandAverageCost(
  totals: NetSuiteItemLocationTotals | NetSuiteItemLocationOnHandTotals | undefined,
  { stale, rowCount }: { stale: boolean; rowCount: number }
): number | null {
  if (stale || rowCount === 0 || !totals || !('averageCost' in totals)) return null;
  return totals.qtyOnHand > 0 ? totals.averageCost : null;
}

/**
 * Assemble the stored figures `selectEstimatedCost` chooses between — FIELD
 * CREDITING ONLY (the lib is the one selector; missing = null, never 0):
 * - itemDefinedCost — DD!97 自訂成本 (product master);
 * - averageCost — DD!119 平均成本 (NetSuite on-hand read-through echo);
 * - lastPurchasePrice — DD!122 上次採購價 (primary vendor line, H51 pair);
 * - purchasePrice — DD!44 credit → primary line `unitCost` (note above).
 */
export function buildCostEstimateInputs(
  product: Product,
  onHandAverageCost: number | null
): CostEstimateInputs {
  const primaryLine = primaryVendorLineOf(product);
  return {
    itemDefinedCost: product.itemDefinedCost ?? null,
    averageCost: onHandAverageCost,
    lastPurchasePrice: primaryLine?.lastPurchasePrice ?? null,
    purchasePrice: primaryLine?.unitCost ?? null,
  };
}

/**
 * Display currency of the credited figure — a metadata LOOKUP, never a
 * conversion (Discribe-19: nothing here is FX-derived): vendor-line figures
 * carry the line currency; itemDefinedCost rides the product currency
 * (CommercialSection `money()` precedent); the on-hand average is a NetSuite
 * echo whose payload carries no currency → null (the tsx renders the bare
 * figure and the SourceStampTag carries provenance instead).
 */
export function costEstimateCurrencyOf(type: CostEstimateType, product: Product): string | null {
  switch (type) {
    case 'item_defined_cost':
      return product.currency ?? null;
    case 'average_cost':
      return null;
    case 'last_purchase_price':
    case 'purchase_price':
      return primaryVendorLineOf(product)?.currency ?? null;
  }
}

export interface ProductCostEstimateView {
  /** DD!96 成本估算類型 assignment — null = unconfigured (explicit empty state). */
  type: CostEstimateType | null;
  /** Lib-selected stored figure — null = figure absent (dash state, never 0-as-data). */
  selection: CostEstimateSelection | null;
  /** Display currency of the credited figure (lookup only, never converted). */
  currency: string | null;
}

const EMPTY_COST_ESTIMATE_VIEW: ProductCostEstimateView = {
  type: null,
  selection: null,
  currency: null,
};

/** DD!96 assignment (dataset) × the credited stored figures → the ONE row the 銷售與定價 tab renders. */
export function buildCostEstimateView(
  dataset: PricingDataset,
  product: Product,
  inputs: CostEstimateInputs
): ProductCostEstimateView {
  const type =
    dataset.productPricing?.find((row) => row.productId === product.id)?.costEstimateType ?? null;
  if (type === null) return EMPTY_COST_ESTIMATE_VIEW;
  return {
    type,
    selection: selectEstimatedCost(type, inputs),
    currency: costEstimateCurrencyOf(type, product),
  };
}

export interface ProductCostEstimate extends ProductCostEstimateView {
  /**
   * On-hand read-through provenance (SourceStampTag + stale badge feed) —
   * meaningful when the credited figure is the NetSuite echo (average_cost).
   */
  readThroughSyncedAt: string | null;
  readThroughStale: boolean;
  isLoading: boolean;
}

/**
 * Everything the PricingSection cost-estimate card renders. Composes the ONE
 * pricing dataset (DD!96 assignment) with the on-hand read-through hook
 * (DD!119 figure; same cacheKey as ItemLocationsSection — ahooks dedupes)
 * and the product prop's stored figures. Selection stays in the pure lib.
 */
export function useProductCostEstimate(product: Product): ProductCostEstimate {
  const { dataset, isLoading } = usePricingDataset();
  const { totals, rows, syncedAt, isLoading: onHandLoading } = useItemLocations(product.id);

  const stale = syncedAt
    ? isStaleTier(freshnessTierOf(syncedAt, new Date().toISOString()))
    : false;

  const view = useMemo<ProductCostEstimateView>(() => {
    if (!dataset) return EMPTY_COST_ESTIMATE_VIEW;
    const averageCost = creditOnHandAverageCost(totals, { stale, rowCount: rows.length });
    return buildCostEstimateView(dataset, product, buildCostEstimateInputs(product, averageCost));
  }, [dataset, product, totals, rows.length, stale]);

  return {
    ...view,
    readThroughSyncedAt: syncedAt ?? null,
    readThroughStale: stale,
    isLoading: isLoading || onHandLoading,
  };
}
